<!--
  NylonOptionPanel — labeled, optionally-footered settings panel chrome.

  Replaces the duplicated `.option-panel` / `-header` / `-body` /
  `-footer` chrome that lived in the submission wizard
  (storefront/create-a-product.vue, 14 sites) and the account edit-
  product mirror (account/edit-product.vue, 6 sites). The chrome
  itself (white card, header band, body padding ramp, footer rule
  line + actions area) is owned here; inner content (titles, help
  icons, form fields, pricing tables, color pickers, etc.) lives in
  the call site via slots.

  Slots:
    #header  — header band content (h4 + optional .help-link / .feedback-icon).
               If empty, the header band element doesn't render.
    #headerControl — a small control that sets something for the whole panel
               (e.g. a <NylonSegmentedControl>). Sits on the heading's row whenever
               the panel is wide enough (a container query on the panel), and on
               its own line under the heading otherwise. Needs #header.
    default  — body content. Required.
    #footer  — footer band content. If empty, the footer band element doesn't render.

  Props:
    embedded (boolean, default false)
      Switches the responsive ramp from "full-screen" (intermediate
      @sm/@md padding/margin breakpoints) to "embedded" (hold base
      values until @lg). Use when the panel lives in a constrained
      horizontal container — the account edit-product layout, modal
      bodies, etc. See _NylonOptionPanel.scss for the future-container-
      query plan that obviates this prop.

    compactFooter (boolean, default false)
      Renders the footer band with the `.nylon-OptionPanel-footer--compact`
      modifier (zero vertical padding). Used for footers that hold a
      single action without needing the standard padded action-bar look.

    tightFooter (boolean, default false)
      Renders the footer band with the `.nylon-OptionPanel-footer--tight`
      modifier (zero margin-top). Used for footers that visually attach
      to the body without the separator space (e.g. the pricing table's
      "Synchronize prices" toggle).

    bodyClass (string | object | array, default null)
      Passed through to the body element. Used for things like
      `{ error: v$.input.meta.title.$invalid }` for vuelidate error states.

    collapsible (boolean, default false)
      Renders a chevron toggle in the header's top-right corner that
      collapses the panel down to its header band. Body + footer sit in
      a <NylonCollapse> region (animated, content stays mounted — so
      inner component state like dropzones and inputs survives). The
      header's corner occupants (.help-link / .feedback-icon) shift
      left to make room.

    startCollapsed (boolean, default false)
      Mount the panel collapsed (collapsible only). For opt-in sections
      like the wizard's Advanced Settings, where the collapsed header IS
      the disclosure affordance.
-->
<template>
  <div
    class="nylon-OptionPanel"
    :class="{
      'nylon-OptionPanel--embedded': embedded,
      'nylon-OptionPanel--withHeaderControl': $slots.headerControl,
      'nylon-OptionPanel--collapsible': collapsible,
      'nylon-OptionPanel--collapsed': collapsible && isCollapsed,
    }"
  >
    <header v-if="$slots.header" class="nylon-OptionPanel-header" :class="{ 'nylon-OptionPanel-header--withControl': $slots.headerControl }">
      <slot name="header" />
      <div v-if="$slots.headerControl" class="nylon-OptionPanel-headerControl">
        <slot name="headerControl" />
      </div>
      <button
        v-if="collapsible"
        type="button"
        class="nylon-OptionPanel-toggle"
        :class="{ 'is-collapsed': isCollapsed }"
        :aria-expanded="(!isCollapsed).toString()"
        aria-label="Toggle panel"
        @click.prevent="toggleCollapsed"
      ></button>
    </header>
    <NylonCollapse :collapsed="collapsible && isCollapsed">
      <div class="nylon-OptionPanel-body" :class="bodyClass">
        <slot />
      </div>
      <footer v-if="$slots.footer" class="nylon-OptionPanel-footer" :class="{ 'nylon-OptionPanel-footer--compact': compactFooter, 'nylon-OptionPanel-footer--tight': tightFooter }">
        <slot name="footer" />
      </footer>
    </NylonCollapse>
  </div>
</template>

<script>
import NylonCollapse from "@/nylon/components/NylonCollapse.vue";

export default {
  name: "NylonOptionPanel",

  components: {
    NylonCollapse,
  },

  props: {
    embedded: { type: Boolean, default: false },
    compactFooter: { type: Boolean, default: false },
    tightFooter: { type: Boolean, default: false },
    bodyClass: { type: [String, Object, Array], default: null },
    collapsible: { type: Boolean, default: false },
    startCollapsed: { type: Boolean, default: false },
  },

  data() {
    return {
      isCollapsed: this.startCollapsed,
    };
  },

  methods: {
    toggleCollapsed() {
      this.isCollapsed = !this.isCollapsed;
    },
  },
};
</script>
